/** @jsxImportSource @emotion/react */
import Modal from "react-modal";
import {
  AllSelectContainer,
  BtnContainer,
  CalendarItemContainer,
  CalendarItemInfoContainer,
  CalendarListContainer,
  CancelContainer,
  Container,
  InfoConatiner,
  PrivateToogleContainer,
  SectionContainer,
  TitleContainer,
  TopSection,
} from "./index.styles";
import UI from "@mile-meeting/mile-ui";
import {
  createGoogleMeetingRoom,
  getConnectedGoogleMeetingRoomList,
  TGoogleMeetingRoom,
} from "apis/meetings/meeting-room";
import { useEffect, useState } from "react";
import { useToast } from "hook/useToast";
import ToolTip from "uis/new-tootip";
import { LoadingModal } from "uis/modal";

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    width: "600px",
    borderRadius: "16px",
    transform: "translate(-50%, -50%)",
    padding: "0",
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};

Modal.setAppElement("#root");

type Props = {
  modalIsOpen: boolean;
  closeModal: () => void;
  googleCalendar?: TGoogleMeetingRoom[];
  googleRefreshToken: string;
  onUpdate?: () => void;
  maxEnableCreateMeetingRoom: number;
};

export default function GoogleCalendarModal(props: Props) {
  const { modalIsOpen, closeModal, googleCalendar, googleRefreshToken, onUpdate } = props;
  const [selectedCalendar, setSelectedCalendar] = useState<TGoogleMeetingRoom[]>([]);
  const [isPrivate, setIsPrivate] = useState<boolean>(true);
  const { showToast } = useToast();
  const [connectedIds, setConnectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (modalIsOpen) {
      setSelectedCalendar([]);
      setIsPrivate(true);
      getConnectedGoogleMeetingRoomList(googleCalendar ?? []).then((res) => {
        setConnectedIds((res.data?.connect ?? []).map((calendar) => calendar.resourceId) ?? []);
      });
    }
  }, [modalIsOpen]);

  const onHandleAllSelect = () => {
    //INFO: 전체 선택
    const aleardyNotSelectedCalendar = googleCalendar?.filter(
      (calendar) => !connectedIds.includes(calendar.resourceId)
    );
    if (selectedCalendar.length === aleardyNotSelectedCalendar?.length) setSelectedCalendar([]);
    else setSelectedCalendar(aleardyNotSelectedCalendar ?? []);
  };

  const isSelectedAll = () => {
    //INFO: 전체 선택 여부
    const aleardyNotSelectedCalendar = googleCalendar?.filter(
      (calendar) => !connectedIds.includes(calendar.resourceId)
    );
    return selectedCalendar.length === aleardyNotSelectedCalendar?.length;
  };

  const onHandleSelect = (calendar: TGoogleMeetingRoom) => {
    //INFO: 선택
    setSelectedCalendar([...selectedCalendar, calendar]);
  };

  const onHandleRegister = async () => {
    //INFO: 등록
    if (selectedCalendar.length > props.maxEnableCreateMeetingRoom) {
      showToast("현재 이용 중이신 플랜에서 생성 가능한 회의실 최대 개수를 초과하여 추가 등록이 불가합니다.", "warning");
      return;
    }
    if (selectedCalendar.length === 0 || loading) return;

    //TODO: 리소스 캘린더 선택 후 연동 버튼 클릭 시: spinner + 안내 문구
    setLoading(true);
    const res = await createGoogleMeetingRoom(googleRefreshToken, isPrivate, selectedCalendar);
    if (res.success) {
      closeModal();
      if (selectedCalendar.length > 1)
        showToast(
          `"${selectedCalendar[0].resourceName}"` +
            `외 ${selectedCalendar.length}개의 ` +
            `리소스 캘린더를 성공적으로 연동 및 등록했습니다.`
        );
      else showToast(`"${selectedCalendar[0].resourceName}" 리소스 캘린더를 성공적으로 연동 및 등록했습니다.`);
      setLoading(false);
      onUpdate?.();
    } else {
      showToast("리소스 캘린더를 연동 및 등록할 수 없습니다. 다시 시도해주세요.", "warning");
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyle}>
      <Container>
        <CancelContainer>
          <UI.IconButton.Normal
            type="primary"
            icon="x"
            size={20}
            color="var(--alias-color-label-normal)"
            onClick={closeModal}
          />
        </CancelContainer>
        <TitleContainer>
          <UI.Icon name="google" size={24} />
          <UI.Typography type="title03" fontStyle="bold" color="var(--alias-color-label-neutral)">
            Google 리소스 캘린더 연동 및 등록
          </UI.Typography>
        </TitleContainer>
        <TopSection>
          <InfoConatiner>
            <UI.Icon name="info" size={24} color="var(--alias-color-primary-normal)" />
            <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
              연동할 리소스 캘린더를 선택해 주세요.
            </UI.Typography>
          </InfoConatiner>
          <SectionContainer>
            <AllSelectContainer onClick={onHandleAllSelect}>
              <UI.CheckBox
                onChange={onHandleAllSelect}
                selected={selectedCalendar.length > 0}
                type={isSelectedAll() ? "selected" : "indeterminate"}
              />
              <UI.Typography
                type="body01 normal"
                fontStyle="medium"
                color="var(--alias-color-label-normal)"
                style={{ cursor: "pointer" }}
              >
                전체 선택
              </UI.Typography>
            </AllSelectContainer>
            <CalendarListContainer>
              {googleCalendar?.map((calendar, index) => (
                <CalendarItem
                  disabled={connectedIds.includes(calendar.resourceId)}
                  key={index}
                  calendar={calendar}
                  onClick={() => {
                    //INFO: 이미 연동된 리소스는 선택 불가
                    if (connectedIds.includes(calendar.resourceId)) return;

                    if (selectedCalendar.includes(calendar)) {
                      setSelectedCalendar(selectedCalendar.filter((item) => item !== calendar));
                    } else onHandleSelect(calendar);
                  }}
                  selected={selectedCalendar.includes(calendar)}
                />
              ))}
            </CalendarListContainer>
          </SectionContainer>
        </TopSection>
        <BtnContainer>
          <PrivateToogleContainer>
            <div className="horizontal1">
              <UI.Icon name="eyeClosed" size={20} color="var(--alias-color-label-normal)" />
              <UI.Typography type="body01 normal" fontStyle="medium" color="var(--alias-color-label-normal)">
                비공개 회의 데이터 수집 및 조회
              </UI.Typography>
              <ToolTip
                icon="info"
                text={
                  <p>
                    활성화 시 일반 회의와 같이 비공개 회의의 모든 데이터를 저장하며, Analytics에서도 조회됩니다.
                    <br />
                    (단, 비공개 회의의 경우 태블릿 상에서는 정보가 표기되지 않습니다.)
                  </p>
                }
                position={{
                  top: 24,
                  left: -250,
                }}
              />
            </div>
            <UI.Selection.Toggle.Default onChange={() => setIsPrivate(!isPrivate)} state={isPrivate} size="medium" />
          </PrivateToogleContainer>
          <UI.Button
            size="large"
            title="등록하기"
            style={{ width: "100%" }}
            onClick={onHandleRegister}
            disabled={selectedCalendar.length <= 0 || selectedCalendar.length > props.maxEnableCreateMeetingRoom}
          />
        </BtnContainer>
      </Container>
      <LoadingModal modalIsOpen={loading} title={"구글 리소스 캘린더를 연동 중입니다."} />
    </Modal>
  );
}

const CalendarItem = ({
  calendar,
  disabled = false,
  onClick,
  selected = false,
}: {
  calendar: TGoogleMeetingRoom;
  disabled?: boolean;
  onClick?: () => void;
  selected?: boolean;
}) => {
  return (
    <CalendarItemContainer
      onClick={onClick}
      style={{
        backgroundColor: disabled
          ? "var(--alias-color-interaction-disabled)"
          : "var(--component-color-background-normal)",
      }}
    >
      <UI.CheckBox disabled={disabled} selected={selected} />
      <CalendarItemInfoContainer>
        <UI.Icon name="google" size={24} style={{ opacity: disabled ? 0.2 : 1 }} />
        <div className="vertical">
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            <UI.Typography
              type="body01 normal"
              fontStyle="medium"
              color={disabled ? "var(--alias-color-label-disabled)" : "var(--alias-color-label-normal)"}
            >
              {calendar.resourceName}
            </UI.Typography>
            {disabled && (
              <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                이미 연동된 리소스입니다.
              </UI.Typography>
            )}
          </div>
          <div className="horizontal">
            <UI.Icon
              name="people"
              size={16}
              color={disabled ? "var(--alias-color-label-disabled)" : "var(--alias-color-label-alternative)"}
            />
            <UI.Typography
              type="label01"
              fontStyle="bold"
              color={disabled ? "var(--alias-color-label-disabled)" : "var(--alias-color-label-alternative)"}
            >
              수용인원: {calendar.capacity}인
            </UI.Typography>
          </div>
        </div>
      </CalendarItemInfoContainer>
    </CalendarItemContainer>
  );
};
